import React, { Dispatch, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { vars } from '../../../../../Theme';
import { ContentLayout } from '../../_parts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import Tabs from '../_parts/Tabs';
import Documents from '../../_parts/Documents';
import { IAppReduxStore } from '../../../../../redux/store';
import { updateSelectedDocuments } from '../../../../../redux/project/documents/sliceProjectSelectedDocuments';
import { fetchViewDataAndDocuments } from '../../../../../util/ProjectUtils';
import { useParams } from 'react-router-dom';
import MainContent from '../_parts/MainContent';
import MainFooterActions from '../_parts/MainFooterActions';
import { includes, pickBy } from 'lodash';
import { useEnqueueSnackbar } from '../../../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../../Toast/Toast';
import {
    getDocuments,
    getIsForwardingQualityGateOpened,
    goToNextForwardingTab,
    onWorkflowTaskReply,
    removeDocumentFromWorkflow,
    resetAfterDirectForward,
} from '../../../../../redux/project/sliceProjectWorkflow';
import DialogFrame from '../../_parts/DialogFrame';
import { DialogContent } from '@mui/material';
import DialogHeader from '../../_parts/DialogHeader';

import AdditionalInputContent, {
    IAdditionalInputTypeEnum,
} from '../../_parts/AdditionalInputContent';
import AdditionalInputFooterActions, {
    IAdditionalInfoResultButtonEnum,
} from '../../_parts/AdditionalInputFooterActions';
import ForwardingQualityGateDialog from '../ForwardingQualityGateDialog/ForwardingQualityGateDialog';

export default ({ open, onClose }: IWorkflowMainDialogProps) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'mainDialog' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Hook for customizing Snackbar. */
    const enqueueSnackbar = useEnqueueSnackbar();

    const params = useParams();
    const { replicaId, areaName } = params;
    const [mainState, setMainState] = React.useState<{
        [key: string]: ForwardState;
    }>({});
    const [additionalInputState, setAdditionalInputState] = React.useState<string[]>([]);
    const [additionalInputErrorState, setAdditionalInputErrorState] =
        React.useState<boolean>(false);
    const [hasError, setHasError] = React.useState(false);

    const {
        isDirectForward,
        totalDirectForwarded,
        completedWorkflowForwardGroup,
        docUnIdsFailedForward,
        isLoadingData,
        additionalInput,
        nextOptionsData,
        selectedTab,
        isForwardingQualityGatePassed,
    } = useSelector((_store: IAppReduxStore) => _store.projectWorkflow);

    const isForwardingQualityGateOpened = useSelector(getIsForwardingQualityGateOpened);

    /** Access relevant state variables from the redux store. */
    const selectedDocuments = useSelector(
        (_store: IAppReduxStore) => _store.projectSelectedDocuments.storage,
    );

    const getForwardedDocumentsToReselect = useMemo(() => {
        if (!nextOptionsData) {
            return [];
        }

        const documentIdsFromOtherTabs = nextOptionsData
            .map((item, index) => {
                if (index <= selectedTab) {
                    return [];
                } else {
                    return item.documents.map(document => document.universalId);
                }
            })
            .flat();

        return [docUnIdsFailedForward, documentIdsFromOtherTabs].flat();
    }, [nextOptionsData, selectedTab, docUnIdsFailedForward]);

    const getStateChanged = (arg: StateChangedArguments) => {
        setHasError(false);
        setMainState(prev => {
            if (!arg.parentValue.includes('<|>options')) {
                const filteredPrev = pickBy(prev, (_, key) => !includes(key, arg.parentValue));
                return { ...filteredPrev, [arg.parentValue]: arg.state };
            } else {
                return { ...prev, [arg.parentValue]: arg.state };
            }
        });
    };

    const onAdditionalInputStateChanged = (value: string[]) => {
        setAdditionalInputErrorState(false);
        setAdditionalInputState(value);
    };

    useEffect(() => {
        if (additionalInput?.response) {
            setAdditionalInputState(additionalInput.response.dialogOptionDefault);
        }
    }, [additionalInput]);

    const showSnackbar = (totalDocuments: number, errorCount: number) => {
        let message = '';
        let severity = null;

        if (totalDocuments === errorCount) {
            message = t('toastError', { numberOfDocuments: errorCount });
            severity = ToastSeverity.Error;
        } else if (errorCount > 0) {
            message = t('toastInfo', {
                totalNumberOfDocuments: totalDocuments,
                forwardedNumberOfDocuments: totalDocuments - errorCount,
            });
            severity = ToastSeverity.Info;
        } else {
            message = t('toastSuccess', { numberOfDocuments: totalDocuments });
            severity = ToastSeverity.Success;
        }

        enqueueSnackbar(message, { severity });
    };

    useEffect(() => {
        if (completedWorkflowForwardGroup) {
            if (areaName !== 'import') {
                dispatch(
                    updateSelectedDocuments({
                        replicaId: selectedDocuments?.replicaId,
                        areaName: selectedDocuments?.areaName,
                        documentIds: getForwardedDocumentsToReselect,
                    }),
                );
            }

            fetchViewDataAndDocuments(params);

            if (!nextOptionsData) {
                !isDirectForward &&
                    showSnackbar(
                        selectedDocuments.documentIds.length,
                        docUnIdsFailedForward?.length,
                    );

                closeDialog();
                return;
            }

            const totalDocumentsInTab = nextOptionsData[selectedTab]?.documents?.length;

            showSnackbar(totalDocumentsInTab, docUnIdsFailedForward?.length);
            if (nextOptionsData.length === selectedTab + 1) {
                closeDialog();
            } else {
                dispatch(goToNextForwardingTab());
            }
        }
    }, [completedWorkflowForwardGroup, docUnIdsFailedForward]);

    useEffect(() => {
        if (isDirectForward) {
            showSnackbar(totalDirectForwarded, docUnIdsFailedForward?.length);

            if (areaName !== 'import') {
                dispatch(
                    updateSelectedDocuments({
                        replicaId: selectedDocuments?.replicaId,
                        areaName: selectedDocuments?.areaName,
                        documentIds: docUnIdsFailedForward,
                    }),
                );
            }

            dispatch(resetAfterDirectForward());
            fetchViewDataAndDocuments(params);
        }
    }, [isDirectForward]);

    useEffect(() => {
        if (isForwardingQualityGatePassed && nextOptionsData?.length === 0) {
            closeDialog();
        }
    }, [isForwardingQualityGatePassed]);

    const documents = useSelector(getDocuments);

    /**
     * Removes documents from workflow.
     * @param documentUniId Document Universal ID.
     */
    const removeDocument = documentUniId => {
        dispatch(
            removeDocumentFromWorkflow({
                documentUniId,
                selectedTab,
                areaName,
            }),
        );
    };

    const handleCancel = () => {
        if (additionalInput) {
            dispatch(
                onWorkflowTaskReply({
                    dialogResultButton: IAdditionalInfoResultButtonEnum.Cancel,
                    additionalInputState: [],
                    replicaId,
                }),
            );
        } else {
            // Close the dialog without performing any action
            closeDialog();
        }
    };

    const closeDialog = () => {
        setMainState({});
        setHasError(false);
        setAdditionalInputState([]);
        setAdditionalInputErrorState(false);
        onClose();
    };

    return (
        <>
            {isForwardingQualityGateOpened ? (
                <ForwardingQualityGateDialog />
            ) : (
                <DialogFrame open={open}>
                    <DialogHeader
                        onClose={handleCancel}
                        dialogTitle={
                            additionalInput
                                ? additionalInput.response.dialogTitle
                                : t('dialogTitle')
                        }
                        chipColor={additionalInput ? null : vars.chipTeal}
                        chipLabel={additionalInput ? null : t('chipLabel')}
                    />
                    <DialogContent sx={{ pt: 0, overflowX: 'hidden' }}>
                        <ContentLayout
                            tabs={additionalInput ? null : <Tabs />}
                            documentSelection={
                                additionalInput ? null : (
                                    <Documents
                                        documents={documents}
                                        removeDocument={removeDocument}
                                    />
                                )
                            }
                            main={
                                additionalInput ? (
                                    <AdditionalInputContent
                                        additionalInputState={additionalInputState}
                                        onAdditionalInputStateChanged={
                                            onAdditionalInputStateChanged
                                        }
                                        hasAdditionalInputError={additionalInputErrorState}
                                        setAdditionalInputErrorState={setAdditionalInputErrorState}
                                        type={IAdditionalInputTypeEnum.Workflow}
                                    />
                                ) : (
                                    <MainContent
                                        getStateChanged={getStateChanged}
                                        hasError={hasError}
                                        mainState={mainState}
                                    />
                                )
                            }
                            loading={isLoadingData}
                        />
                    </DialogContent>
                    {additionalInput ? (
                        <AdditionalInputFooterActions
                            additionalInputState={additionalInputState}
                            setAdditionalInputErrorState={setAdditionalInputErrorState}
                            type={IAdditionalInputTypeEnum.Workflow}
                        />
                    ) : (
                        <MainFooterActions
                            mainState={mainState}
                            getStateChanged={getStateChanged}
                            setHasError={setHasError}
                        />
                    )}
                </DialogFrame>
            )}
        </>
    );
};

/**
 * Interface that describes WorkflowMainDialog's props.
 */
interface IWorkflowMainDialogProps extends React.PropsWithChildren {
    open: boolean;
    onClose?: () => void;
}

export interface ForwardState {
    multi: string[];
    exclusive: string[];
}

export interface StateChangedArguments {
    parentValue: string;
    state: ForwardState;
}
